import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Hero from '@/components/Hero';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Lightbulb, Heart, Infinity as InfinityIcon, ArrowRight } from 'lucide-react';
import OptimizedImage from '@/components/OptimizedImage';
import { ReviewSection } from '@/components/ReviewSection';
import { familieopstellingReviews } from '@/data/reviews';
import homeReviewsPanorama from '@/assets/systemique/home-reviews-panorama.webp';
import GrainTexture from '@/components/GrainTexture';
import yinyangbruin from '@/assets/yinyangbruin.png';
import yinyangbruinWebp from '@/assets/yinyangbruin.webp';
import yinyangpaars from '@/assets/yinyangpaars.png';
import yinyangpaarsWebp from '@/assets/yinyangpaars.webp';
import { SEO } from '@/components/SEO';
import { HOME_PAGE_JSON_LD } from '@/seo/homePageJsonLd';

const Index = () => {
  const navigate = useNavigate();
  
  return (
    <>
      <SEO 
        title="Organisatieopstellingen & Teamcoaching | Voor leiders en managers"
        description="Systemisch werk voor organisaties. Organisatieopstellingen, teamcoaching en systemische leergang voor leiders. Doorbreek patronen in teams en versterk leiderschap in Castricum, Amsterdam en Noord-Holland."
        keywords="systemische coaching Castricum, systemische coach Amsterdam, systemisch werk Haarlem, familieopstellingen Zaanstreek, systemische coaching Noord-Holland, systemisch werk Alkmaar, organisatieopstellingen Amsterdam, systemische coach Zaandam, teamcoaching systemisch, systemisch werk Nederland"
        canonical="https://organisatieopstellingen.com/"
        jsonLd={HOME_PAGE_JSON_LD}
      />
      
      <Hero />

      {/* Answer-first: GEO citability */}
      <section className="py-12 md:py-16 px-6 bg-white relative">
        <div className="container mx-auto max-w-3xl text-center">
          <h2 className="font-serif text-2xl md:text-3xl text-[#854C73] mb-6">
            Wat biedt Organisatieopstellingen?
          </h2>
          <p className="text-[#6b4912] text-lg leading-relaxed font-light">
            <strong>Organisatieopstellingen</strong> is het zakelijke platform van Marthe van der Molen voor{' '}
            <strong>systemisch werk in organisaties</strong>: organisatieopstellingen, systemische teamcoaching en
            de leergang systemisch leiderschap (<em>Duiken in de Onderstroom</em>). Een organisatieopstelling duurt
            1,5 tot 2 uur per casus; teamtrajecten vaak een dagdeel tot een hele dag. De leergang bestaat uit 3
            modules (max. 8 deelnemers, € 1.500 excl. btw). Sessies in Castricum, Amsterdam, Haarlem en op locatie
            in heel Nederland.
          </p>
          <p className="text-[#6b4912] text-lg leading-relaxed font-light mt-6">
            <Link
              to="/organisatieopstellingen-noord-holland"
              className="text-[#854C73] underline underline-offset-4 hover:text-[#590D0D] font-medium"
            >
              Organisatieopstellingen in Noord-Holland
            </Link>
            : werkgebied, praktische info en veelgestelde vragen.
          </p>
        </div>
      </section>
      
      {/* Intro Cards */}
      <section id="wat-brengt-je-hier" className="py-24 px-6 bg-[#854C73] text-white overflow-hidden relative">
        <GrainTexture opacity={0.15} />
        {/* Rotating circle diagram with gradient blur */}
        <div className="absolute top-1/2 right-0 -translate-y-1/2 w-96 h-96 opacity-20">
          <div className="w-full h-full rounded-full border-2 border-white/30 animate-spin-slow relative">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 rounded-full bg-gradient-to-tr from-[#F9EBEF]/20 via-[#854C73]/10 to-transparent blur-3xl"></div>
          </div>
        </div>
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="mb-20">
            {/* Large left-aligned heading */}
            <h2 className="font-serif text-4xl md:text-6xl mb-6 leading-tight">
              Wat zoek je <span className="italic font-light font-serif text-[#F9EBEF] opacity-80">precies?</span>
            </h2>
            {/* Body text - left aligned, white */}
            <div className="max-w-3xl">
              <p className="text-white text-lg md:text-xl leading-relaxed" style={{ fontFamily: "'Noto Serif Display', serif" }}>
                Hieronder zie je hoe we teams, leiders en organisaties verder helpen.
              </p>
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              { title: "Systemische leergang voor leiders", desc: "3-daagse leergang Duiken in de Onderstroom. Ontwikkel systemisch leiderschap en leer pragmatisch systemisch kijken en handelen.", link: '/leergang-onderstroom', buttonText: "Bekijk de leergang" },
              { title: "Organisatieopstellingen", desc: "Kijk voorbij de symptomen. Zie direct waar de blokkade zit en waar de oplossing ligt.", link: '/organisatieopstellingen', buttonText: "Naar opstellingen" },
              { title: "Teamcoaching", desc: "Versterk de band in je team. Voor leiders en teams die steviger willen staan.", link: '/teamcoaching', buttonText: "Naar coaching" },
              { title: "Opleidingen & maatwerk", desc: "Maatwerktrajecten voor teams en organisaties die systemisch werk structureel willen inbedden.", link: '/opleidingstrajecten', buttonText: "Bekijk opleidingen" }
            ].map((card, i) => {
              const altTexts = [
                "Systemische leergang voor leiders, Duiken in de Onderstroom",
                "Organisatieopstellingen, systemisch werk voor teams en organisaties",
                "Systemische teamcoaching, versterk samenwerking in je team",
                "Opleidingstrajecten systemisch werk, maatwerk voor teams en organisaties"
              ];
              return (
                <div key={i} onClick={() => navigate(card.link)} className="bg-[#F9EBEF] text-[#854C73] p-10 rounded-2xl hover:-translate-y-2 transition-transform duration-300 shadow-xl cursor-pointer group animate-pulse-border relative overflow-visible">
                  <OptimizedImage 
                    src={i === 0 || i === 2 ? yinyangbruin : yinyangpaars} 
                    webpSrc={i === 0 || i === 2 ? yinyangbruinWebp : yinyangpaarsWebp} 
                    alt={altTexts[i]} 
                    className="w-12 h-12 mb-6 opacity-80 group-hover:opacity-100 transition-opacity" 
                    width={48}
                    height={48}
                  />
                  <h3 className="font-serif text-2xl font-bold mb-4 group-hover:text-[#854C73] transition-colors">{card.title}</h3>
                  <p className="text-sm leading-relaxed opacity-80 mb-8 font-light">{card.desc}</p>
                  <div className="flex items-center text-sm font-bold uppercase tracking-wider border-b border-[#854C73]/20 pb-1 w-fit group-hover:border-[#854C73] transition-colors text-[#854C73]">
                    {card.buttonText || "Ontdek meer"} <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" aria-hidden="true" />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Leergang CTA */}
      <section className="py-16 md:py-20 px-6 bg-[#FAEBEE] relative">
        <GrainTexture opacity={0.1} />
        <div className="container mx-auto max-w-4xl relative z-10">
          <Link to="/leergang-onderstroom" className="block group">
            <div className="relative rounded-[2rem] overflow-hidden bg-[#6b4912] p-8 md:p-10 shadow-xl hover:shadow-2xl transition-all duration-300 text-white">
              <div className="relative z-10">
                <Badge className="border border-white/30 text-white bg-transparent mb-4">
                  3-daagse leergang · voorjaar 2026
                </Badge>
                <h2 className="font-serif text-3xl md:text-4xl text-[#F9EBEF] mb-4 group-hover:underline">
                  Systemische leergang voor <span className="italic font-light">leiders</span>
                </h2>
                <p className="text-white/90 text-base md:text-lg leading-relaxed font-light mb-6 max-w-2xl">
                  Duiken in de Onderstroom: pragmatisch systemisch leren kijken en handelen. Voor leiders en adviseurs die de systemische bril willen toevoegen aan hun praktijk.
                </p>
                <div className="flex items-center text-white font-medium text-sm uppercase tracking-wider">
                  <span>Bekijk leergang en data</span>
                  <ArrowRight size={16} className="ml-2 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </div>
          </Link>
        </div>
      </section>

      {/* Wat is systemisch werk? - Met visuele elementen */}
      <section className="py-12 md:py-24 bg-[#FAEBEE] px-6 relative">
        <GrainTexture opacity={0.1} />
        <div className="container mx-auto max-w-6xl relative z-10">
          <div className="grid md:grid-cols-2 gap-6 md:gap-12 items-center">
            {/* Linker kolom: Tekst */}
            <div>
              <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-[#854C73] mb-4 md:mb-6">
                Wat is systemisch werk voor <span className="italic font-light font-serif">organisaties</span>?
              </h2>
              
              <div className="space-y-4 md:space-y-6 text-[#6b4912]">
                <p className="text-base md:text-lg leading-relaxed">
                  <strong>Systemisch werk voor organisaties</strong> is een methodiek die kijkt naar de onderstroom:
                  onzichtbare patronen, loyaliteiten en verstrikkingen in teams en bedrijven. Via organisatieopstellingen
                  of systemische teamcoaching maak je die dynamiek zichtbaar, zodat je niet alleen symptomen aanpakt maar
                  de bron vindt. Marthe van der Molen werkt vanuit Castricum voor leiders in Noord-Holland (Amsterdam,
                  Haarlem, Alkmaar, Zaandam) en op locatie in heel Nederland.
                </p>
                
                <div className="pt-4">
                  <Button asChild variant="default" size="lg" className="bg-[#854C73] shadow-lg hover:scale-105 before:absolute before:inset-0 before:bg-[#590D0D] before:translate-x-[-100%] before:transition-transform before:duration-300 hover:before:translate-x-0 before:z-0 w-full md:w-auto">
                    <Link to="/aanbod" className="relative z-20 text-white">
                      <span className="relative z-20 text-white">lees meer over mijn werkwijze</span>
                    </Link>
                  </Button>
                </div>
              </div>
            </div>
            
            {/* Rechter kolom: Visueel element met bestaande voordelen */}
            <div className="relative mt-8 md:mt-0">
              <div className="bg-white rounded-2xl p-6 md:p-8 shadow-xl border border-[#FAEBEE]">
                <div className="mb-4 md:mb-6">
                  <Badge className="mb-2">Waarom werkt dit zo goed?</Badge>
                  <h3 className="font-serif text-xl md:text-2xl text-[#854C73]">De kracht van de onderstroom.</h3>
                </div>
                
                <div className="space-y-4">
                  {[
                    { title: "Voorbij de woorden", desc: "Waarom maanden vergaderen? Vaak maakt één sessie al inzichtelijk waar de schoen wringt.", icon: Lightbulb },
                    { title: "Visueel inzicht", desc: "Als je het probleem letterlijk voor je ziet staan in de ruimte, snap je direct wat er moet gebeuren.", icon: Heart },
                    { title: "Blijvende verandering", desc: "We gaan voor de lange termijn. We herstellen de balans zodat het team weer gezond verder kan.", icon: InfinityIcon }
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-3 md:gap-4">
                      <div className="w-10 h-10 md:w-12 md:h-12 bg-[#FAEBEE] rounded-full flex items-center justify-center text-[#854C73] shrink-0">
                        <item.icon size={20} className="md:w-6 md:h-6" />
                      </div>
                      <div>
                        <h4 className="font-serif text-lg md:text-xl text-[#854C73] mb-1 md:mb-2">{item.title}</h4>
                        <p className="text-[#6b4912] text-sm md:text-base leading-relaxed">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="bg-[#FAEBEE]">
        <div className="px-4 sm:px-6 pt-12 sm:pt-16">
          <div className="max-w-xl mx-auto">
            <div className="group rounded-2xl overflow-hidden shadow-md">
              <div className="aspect-[21/9] overflow-hidden">
                <OptimizedImage
                  src={homeReviewsPanorama}
                  webpSrc={homeReviewsPanorama}
                  alt="Sfeerbeeld van een systemische sessie, organisatieopstellingen en teamcoaching"
                  className="w-full h-full object-cover object-center transition-transform duration-700 ease-out group-hover:scale-[1.04]"
                  width={1600}
                  height={1067}
                />
              </div>
            </div>
          </div>
        </div>

        <ReviewSection
          reviews={familieopstellingReviews}
          compact={true}
          showStructuredData={false}
        />
      </div>

      {/* CTA Section - simplified for now, can be enhanced later */}
      <section className="relative py-24 overflow-hidden" style={{ backgroundColor: '#efe8dc' }}>
        <GrainTexture opacity={0.15} />
        <div className="container mx-auto px-6 text-center relative z-10">
          <div className="max-w-3xl mx-auto animate-fade-up">
            <h2 className="font-serif text-4xl md:text-5xl mb-6" style={{ color: '#6b4912' }}>Wil je weer <span className="italic font-light font-serif">beweging</span> in je zaak?</h2>
            <p className="text-lg mb-8 leading-relaxed" style={{ color: '#6b4912' }}>
              Laten we bellen. Dan kijken we samen wat systemisch werk voor jouw team kan doen.
            </p>
            <div className="flex flex-col md:flex-row gap-4 justify-center">
              <Button asChild variant="white" size="lg" className="bg-white" style={{ color: '#854C73' }}>
                <Link to="/contact?onderwerp=kennismakingsgesprek">Plan een kennismaking</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Index;
